import type RAPIER from '@dimforge/rapier3d-compat';
import type { SurfaceName } from './surfaces.js';
import { type VehicleInput, EMPTY_INPUT } from './input.js';
import { Vehicle, type VehicleConfig } from './vehicle.js';
import { v3, type Vec3 } from './math.js';

export interface ColliderUserData {
  kind: 'world' | 'vehicle';
  surface?: SurfaceName;
  vehicleId?: number;
}

export const FIXED_DT = 1 / 60;
export const FIXED_DT_MS = 1000 / 60;

export interface SimWorldOptions {
  /** Rapier module. Pass the already-initialized module (await rapier.init()). */
  rapier: typeof RAPIER;
  /** World gravity. Sim only uses this if a vehicle is airborne; default = 0 (vehicles manage their own). */
  gravity?: Vec3;
  fixedDt?: number;
}

/**
 * SimWorld is shared between client and server:
 *  - Client uses it for prediction + remote-car interpolation rest.
 *  - Server uses it as the authoritative truth.
 *
 * It owns a Rapier world (collision queries only), a map of Vehicles, and the
 * fixed-tick advance.
 */
export class SimWorld {
  readonly rapier: typeof RAPIER;
  readonly world: RAPIER.World;
  readonly fixedDt: number;

  tick = 0;
  private vehicles = new Map<number, Vehicle>();
  private inputs = new Map<number, VehicleInput>();

  constructor(opts: SimWorldOptions) {
    this.rapier = opts.rapier;
    this.fixedDt = opts.fixedDt ?? FIXED_DT;
    const g = opts.gravity ?? v3(0, 0, 0);
    this.world = new opts.rapier.World(g);
    // Even tho vehicles do their own gravity, we run the Rapier step too so
    // static colliders + ray queries stay consistent and any future
    // dynamic-body props (debris, ramps) work.
    this.world.timestep = this.fixedDt;
  }

  /** Add a static trimesh collider with a tagged surface. */
  addStaticTrimesh(
    vertices: Float32Array,
    indices: Uint32Array,
    surface: SurfaceName = 'road',
  ): RAPIER.RigidBody {
    const rb = this.world.createRigidBody(this.rapier.RigidBodyDesc.fixed());
    const cd = this.rapier.ColliderDesc.trimesh(vertices, indices);
    this.world.createCollider(cd, rb);
    const ud: ColliderUserData = { kind: 'world', surface };
    rb.userData = ud;
    return rb;
  }

  /** Add a static cuboid (handy for prototype ramps / pads). */
  addStaticCuboid(
    center: Vec3,
    halfExtents: Vec3,
    surface: SurfaceName = 'road',
  ): RAPIER.RigidBody {
    const rb = this.world.createRigidBody(
      this.rapier.RigidBodyDesc.fixed().setTranslation(center.x, center.y, center.z),
    );
    const cd = this.rapier.ColliderDesc.cuboid(halfExtents.x, halfExtents.y, halfExtents.z);
    this.world.createCollider(cd, rb);
    const ud: ColliderUserData = { kind: 'world', surface };
    rb.userData = ud;
    return rb;
  }

  addVehicle(id: number, config?: Partial<VehicleConfig>): Vehicle {
    const v = new Vehicle(id, config);
    // Build a kinematic-position-based body so it shows up in shape-casts
    // (for car-vs-car) but Rapier doesn't try to simulate it dynamically.
    const rb = this.world.createRigidBody(
      this.rapier.RigidBodyDesc.kinematicPositionBased(),
    );
    const cd = this.rapier.ColliderDesc.cuboid(
      v.config.halfExtents.x,
      v.config.halfExtents.y,
      v.config.halfExtents.z,
    );
    this.world.createCollider(cd, rb);
    const ud: ColliderUserData = { kind: 'vehicle', vehicleId: id };
    rb.userData = ud;
    v.attachKinematicBody(rb);
    this.vehicles.set(id, v);
    return v;
  }

  removeVehicle(id: number): void {
    const v = this.vehicles.get(id);
    if (v) {
      const rb = v.kinematicBody;
      if (rb) this.world.removeRigidBody(rb);
    }
    this.vehicles.delete(id);
    this.inputs.delete(id);
  }

  getVehicle(id: number): Vehicle | undefined {
    return this.vehicles.get(id);
  }

  getAllVehicles(): IterableIterator<Vehicle> {
    return this.vehicles.values();
  }

  setInput(id: number, input: VehicleInput): void {
    this.inputs.set(id, input);
  }

  /** Advance one fixed tick. */
  step(): void {
    for (const veh of this.vehicles.values()) {
      const inp = this.inputs.get(veh.id) ?? EMPTY_INPUT;
      veh.step(this.rapier, this.world, inp, this.fixedDt, this.vehicles);
      veh.syncKinematicBody();
    }
    this.world.step();
    this.tick++;
  }

  /**
   * Accumulator-driven update. Call from render loop with real-time dt;
   * step() is invoked 0..N times this frame to keep up. Returns the
   * fractional remainder for visual interpolation (`alpha`).
   */
  accumulator = 0;
  /**
   * Accumulator-driven update. maxSteps is generous so a single render frame
   * can absorb a long real-time gap (e.g., headless screenshot virtual-time
   * compression, or a real user's lag spike) without the sim freezing.
   */
  advance(realDtSec: number, maxSteps = 1500): number {
    this.accumulator += realDtSec;
    let steps = 0;
    while (this.accumulator >= this.fixedDt && steps < maxSteps) {
      this.step();
      this.accumulator -= this.fixedDt;
      steps++;
    }
    if (steps === maxSteps) {
      this.accumulator = 0;
    }
    return this.accumulator / this.fixedDt;
  }
}
